import { ScrollingModule } from '@angular/cdk/scrolling';
import { Component, OnInit, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, distinctUntilChanged, map } from 'rxjs';
import { Trade } from 'src/app/components/trades/trades.model';
import { TradesService } from 'src/app/services/trades.service';
import { TableDataState } from 'src/app/store/table-data/table-data.reducer';
import {
  BasisPrice,
  CashPrice,
  MarketType,
  PriceUnion,
} from '../../lots.model';
import { LotsService } from 'src/app/services/lots.service';
import { subscribe } from 'graphql';
import { PriceTypePipe } from 'src/app/pipes/price-type.pipe';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { BlockUserRolesDirective } from 'src/app/directives/block-user-roles.directive';
import { DomesticPipe } from 'src/app/pipes/domestic-city.pipe';
import { SameYearPipe } from 'src/app/pipes/same-year.pipe';
import { LotDetailsCardComponent } from '../lot-details-card/lot-details-card.component';
import { NegotiationDetailsCardComponent } from '../negotiation-details-card/negotiation-details-card.component';
import { selectTableData } from 'src/app/store/table-data/table-data.selectors';

@Component({
  selector: 'app-trades-viewer',
  templateUrl: './trades-viewer.component.html',
  styleUrl: './trades-viewer.component.css',
  imports: [
    CommonModule,
    ScrollingModule,
    PriceTypePipe,
    PriceTypePipe,
    RouterModule,
    MatCardModule,
    MatChipsModule,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatTableModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    FontAwesomeModule,
  ],
})
export class TradesViewerComponent implements OnInit {
  trades$: Observable<Trade[]>;
  currentMarket = signal<MarketType>(null);
  marketType$: Observable<MarketType>;

  constructor(
    private tradesService: TradesService,
    private lotsService: LotsService,
    private store: Store<{ lotTableData: TableDataState }>
  ) {}

  ngOnInit(): void {
    this.store
      .select(selectTableData)
      .pipe(
        distinctUntilChanged(),
        map((res) => {
          this.currentMarket.set(MarketType[res.selectedMarket]);
          this.getTrades();
        })
      )
      .subscribe();
  }

  getTrades() {
    // fetch trades
    this.trades$ = this.tradesService
      .watch({ limit: 10, market: this.currentMarket() })
      .valueChanges.pipe(
        map((res) => {
          const trades = [...res.data.trades];
          // sort by date, commodity, price, and volume
          trades.sort((a, b) => {
            let priceA: number;
            let priceB: number;
            if (this.lotsService.isBasisPrice(a.price)) {
              priceA = (a.price as BasisPrice).basisPoints;
            } else if (this.lotsService.isCashPrice(a.price)) {
              priceA = (a.price as CashPrice).amount;
            }

            if (this.lotsService.isBasisPrice(b.price)) {
              priceB = (b.price as BasisPrice).basisPoints;
            } else if (this.lotsService.isCashPrice(b.price)) {
              priceB = (b.price as CashPrice).amount;
            }

            let volumeA = Number(a.volume_value);
            let volumeB = Number(b.volume_value);

            const dateComparison = b.createdAt.localeCompare(a.createdAt);
            let typeComparison = a.commodity.localeCompare(b.commodity);
            let priceComparison = priceB - priceA;
            let volumeComparison = volumeB - volumeA;

            return (
              dateComparison ||
              typeComparison ||
              priceComparison ||
              volumeComparison
            );
          });
          return trades;
        })
      );
  }
}
